'use client';

import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import {
  type ChangeEvent,
  type KeyboardEvent,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTypewriter } from '@/lib/hooks/useTypewriter';
import { Icon } from '@/lib/ui-kit/icons';
import { cn } from '@/lib/ui-kit/utils';
import { Kbd } from './shared/Kbd';
import { PANEL_CLOSE_EASE_CLASS, PANEL_EASE_CLASS } from './shared/motion';

const CARET_BLINK = { opacity: [1, 1, 0, 0] };
const CARET_TIMING = { duration: 1, times: [0, 0.5, 0.5, 1], repeat: Infinity };
const APPLE_PLATFORM = /Mac|iPhone|iPad|iPod/i;

/** ⌘K on Apple keyboards, Ctrl K elsewhere — known only in the browser, so none on the server. */
function useShortcutLabel(): string | null {
  const [label, setLabel] = useState<string | null>(null);
  useEffect(() => {
    setLabel(APPLE_PLATFORM.test(navigator.userAgent) ? '⌘K' : 'Ctrl K');
  }, []);
  return label;
}

interface HeaderSearchFieldProps {
  open: boolean;
  value: string;
  placeholder: string;
  /** Real queries typed out while the field sits idle. */
  placeholders: string[];
  shortcutHit: boolean;
  inputRef: RefObject<HTMLInputElement | null>;
  listId: string;
  activeId: string | undefined;
  onChange: (value: string) => void;
  onFocus: () => void;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  onClear: () => void;
}

/**
 * The header's 32px field (Figma 14629:171374), which grows into the panel's
 * 44px top bar on open. Idle, it types real queries and names its shortcut.
 */
export function HeaderSearchField({
  open,
  value,
  placeholder,
  placeholders,
  shortcutHit,
  inputRef,
  listId,
  activeId,
  onChange,
  onFocus,
  onKeyDown,
  onClear,
}: HeaderSearchFieldProps) {
  const t = useTranslations('search');
  const typedRef = useRef<HTMLSpanElement>(null);
  const shortcut = useShortcutLabel();
  const idle = !open && !value;
  const typing = idle && placeholders.length > 0;

  // Writes each character straight into the span below — no per-character re-render.
  useTypewriter(typedRef, placeholders, { enabled: typing });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value);

  return (
    <div
      className={cn(
        'relative flex cursor-text items-center gap-2 border border-solid pr-10',
        'transition-[height,padding,border-color,border-radius,background-color]',
        open ? PANEL_EASE_CLASS : PANEL_CLOSE_EASE_CLASS,
        // Open, the bar is the panel's top: no bottom edge, the dropdown continues it.
        open
          ? 'h-11 rounded-t-[14px] rounded-b-none border-v2-border border-b-transparent bg-v2-surface-secondary pl-3.5 dark:border-v2-surface-tertiary dark:border-b-transparent'
          : 'h-8 rounded-[10px] border-v2-btn-secondary-hover bg-transparent pl-[13px] hover:border-v2-text-secondary/60',
      )}
    >
      <Icon
        name="Search"
        size="16px"
        className={cn(
          'shrink-0 transition-colors',
          open ? 'text-v2-text-primary' : 'text-v2-icon-primary',
        )}
        aria-hidden
      />
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open ? activeId : undefined}
        aria-autocomplete="list"
        aria-label={placeholder}
        autoComplete="off"
        spellCheck={false}
        value={value}
        placeholder={placeholder}
        onChange={handleChange}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        className={cn(
          'h-full min-w-0 flex-1 bg-transparent text-[13px] leading-none text-v2-text-primary caret-v2-bg-blue outline-none placeholder:text-v2-text-secondary',
          // Idle, the typed suggestions stand in for the placeholder.
          typing && 'placeholder:text-transparent',
        )}
      />
      {typing ? (
        <span
          aria-hidden
          className="pointer-events-none absolute left-[37px] flex items-center whitespace-nowrap text-[12px] leading-[1.15] text-v2-text-secondary"
        >
          <span ref={typedRef} />
          <motion.span
            className="ml-px inline-block h-[13px] w-px bg-current"
            animate={CARET_BLINK}
            transition={CARET_TIMING}
          />
        </span>
      ) : null}
      {idle && shortcut ? (
        // A flex box, not an inline span: inline, the cap sat on the text baseline, below centre.
        <span className="absolute top-1/2 right-[7px] flex -translate-y-1/2">
          <Kbd hit={shortcutHit}>{shortcut}</Kbd>
        </span>
      ) : null}
      {value ? (
        <button
          type="button"
          tabIndex={-1}
          aria-label={t('clearSearch')}
          onClick={onClear}
          className="absolute top-1/2 right-2 flex size-6 -translate-y-1/2 cursor-pointer items-center justify-center text-v2-text-secondary transition-colors hover:text-v2-text-primary"
        >
          <Icon name="Close" size={14} aria-hidden />
        </button>
      ) : null}
    </div>
  );
}
